import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';

const Alert = ({ type, message, duration = 5000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (duration) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        onClose?.();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  if (!isVisible) return null;

  const config = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      iconColor: 'text-green-400'
    },
    error: {
      icon: XCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      iconColor: 'text-red-400'
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-800',
      iconColor: 'text-yellow-400'
    },
    info: {
      icon: Info,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      iconColor: 'text-blue-400'
    }
  }[type] || config.info;

  const Icon = config.icon;

  return (
    <div className={`fixed top-4 right-4 z-50 max-w-md ${config.bgColor} border ${config.borderColor} rounded-lg shadow-lg transform transition-transform duration-300 ease-in-out`}>
      <div className="flex items-start p-4">
        <Icon className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${config.iconColor}`} />
        <div className="flex-1">
          <p className={`text-sm font-medium ${config.textColor}`}>{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            onClose?.();
          }}
          className={`ml-4 ${config.textColor} hover:opacity-75`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Static methods for easy use
Alert.success = (message, duration) => {
  const event = new CustomEvent('show-alert', {
    detail: { type: 'success', message, duration }
  });
  window.dispatchEvent(event);
};

Alert.error = (message, duration) => {
  const event = new CustomEvent('show-alert', {
    detail: { type: 'error', message, duration }
  });
  window.dispatchEvent(event);
};

Alert.warning = (message, duration) => {
  const event = new CustomEvent('show-alert', {
    detail: { type: 'warning', message, duration }
  });
  window.dispatchEvent(event);
};

Alert.info = (message, duration) => {
  const event = new CustomEvent('show-alert', {
    detail: { type: 'info', message, duration }
  });
  window.dispatchEvent(event);
};

// Alert Container Component
export const AlertContainer = () => {
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    const handleAlert = (event) => {
      const { type, message, duration } = event.detail;
      const id = Date.now();
      setAlerts(prev => [...prev, { id, type, message, duration }]);
    };

    window.addEventListener('show-alert', handleAlert);
    return () => window.removeEventListener('show-alert', handleAlert);
  }, []);

  const handleClose = (id) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  };

  return (
    <>
      {alerts.map(alert => (
        <Alert
          key={alert.id}
          type={alert.type}
          message={alert.message}
          duration={alert.duration}
          onClose={() => handleClose(alert.id)}
        />
      ))}
    </>
  );
};

export default Alert;